import { createClient } from '@supabase/supabase-js'
import { useStore } from './store'

export const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL!,
  process.env.REACT_APP_SUPABASE_ANON_KEY!
)

/**
 * In dev the CRA proxy forwards /api/* to localhost:8000, so API_BASE is empty.
 * In production set REACT_APP_API_URL if the backend is on a different origin.
 */
export const API_BASE = process.env.REACT_APP_API_URL || ''

/**
 * Read the access token from Zustand memory and return it as an Authorization header.
 * The token is never stored in localStorage — it lives only in memory and is cleared
 * on page refresh (restored via the HttpOnly refresh cookie on next load).
 */
function getAuthHeader(): Record<string, string> {
  const token = useStore.getState().accessToken
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/**
 * On a 401, the in-memory access token has expired.
 * Call /api/auth/refresh with credentials:'include' (sends the HttpOnly refresh cookie)
 * to get a new access token, then store it in Zustand memory.
 */
async function tryRefresh(): Promise<boolean> {
  try {
    const r = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',  // Send the HttpOnly sb_refresh cookie
    })
    if (!r.ok) return false
    const data = await r.json()
    if (!data.access_token) return false
    useStore.getState().setAccessToken(data.access_token)
    // Also restore user info if it was cleared
    if (data.user_id && data.email) {
      useStore.getState().setAuth({
        id: data.user_id,
        email: data.email,
        org_id: data.org_id || '',
        role: data.role || 'member',
        status: data.status || 'pending',
      })
    }
    return true
  } catch {
    return false
  }
}

function dispatchSessionExpired() {
  window.dispatchEvent(new CustomEvent('auth:session-expired'))
}

async function fetchWithAuth(url: string, init: RequestInit, isRetry = false): Promise<Response> {
  // Auth endpoints need credentials:'include' to set/send the HttpOnly refresh cookie.
  // Other endpoints use Bearer token auth only — no cookies needed.
  const needsCookies = url.includes('/api/auth/')
  const res = await fetch(url, {
    ...init,
    credentials: needsCookies ? 'include' : 'same-origin',
    headers: {
      ...getAuthHeader(),
      ...(init.headers as Record<string, string> || {}),
    },
  })

  if (res.status === 401 && !isRetry) {
    const refreshed = await tryRefresh()
    if (refreshed) {
      return fetchWithAuth(url, init, true)
    }
    // Only show "session expired" toast if the user was previously logged in
    if (useStore.getState().accessToken) {
      dispatchSessionExpired()
      throw new Error('Session expired. Please log in again.')
    }
    throw new Error('Invalid email or password.')
  }

  return res
}

export class ApiError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

async function throwIfNotOk(r: Response) {
  if (!r.ok) {
    const text = await r.text()
    let message = text
    try {
      const json = JSON.parse(text)
      // FastAPI wraps validation/HTTP errors as {"detail": "..."}
      if (json.detail) message = typeof json.detail === 'string' ? json.detail : JSON.stringify(json.detail)
    } catch { /* not JSON — use raw text */ }
    throw new ApiError(message, r.status)
  }
}

/** Low-level authenticated fetch — returns raw Response for streaming/blob endpoints. */
export async function apiFetch(path: string, init: RequestInit = {}) {
  return fetchWithAuth(`${API_BASE}${path}`, init)
}

export async function apiGet(path: string) {
  const r = await fetchWithAuth(`${API_BASE}${path}`, {})
  await throwIfNotOk(r)
  return r.json()
}

export async function apiPost(path: string, body: unknown) {
  const r = await fetchWithAuth(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  await throwIfNotOk(r)
  return r.json()
}

export async function apiPatch(path: string, body: unknown) {
  const r = await fetchWithAuth(`${API_BASE}${path}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  await throwIfNotOk(r)
  return r.json()
}

export async function apiDelete(path: string) {
  const r = await fetchWithAuth(`${API_BASE}${path}`, { method: 'DELETE' })
  await throwIfNotOk(r)
  return r.json()
}

export async function apiUpload(path: string, formData: FormData) {
  // No Content-Type header — browser sets multipart/form-data with boundary automatically
  const r = await fetchWithAuth(`${API_BASE}${path}`, {
    method: 'POST',
    body: formData,
  })
  await throwIfNotOk(r)
  return r.json()
}

/**
 * Bootstrap auth on page load.
 * Calls /api/auth/refresh with the HttpOnly cookie → returns a new access token.
 * Returns the user object on success, null if no valid session exists.
 */
export async function bootstrapAuth(): Promise<{ id: string; email: string; org_id: string; role: string; status: string } | null> {
  try {
    const r = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    })
    if (!r.ok) return null
    const data = await r.json()
    if (!data.access_token) return null
    useStore.getState().setAccessToken(data.access_token)
    return {
      id: data.user_id,
      email: data.email,
      org_id: data.org_id || '',
      role: data.role || 'member',
      status: data.status || 'pending',
    }
  } catch {
    return null
  }
}
